import React, { useEffect, useState, useCallback } from 'react';
import {
    Card,
    CardHeader,
    Title2,
    Title3,
    Body1,
    Button,
    Switch,
    Slider,
    Badge,
    Spinner,
    MessageBar,
    MessageBarBody,
    tokens,
    makeStyles,
    Field,
    Select,
    Input
} from '@fluentui/react-components';
import {
    CheckmarkCircle24Regular,
    DismissCircle24Regular,
    Settings24Regular,
    Play24Regular,
    ArrowSync24Regular
} from '@fluentui/react-icons';

const useStyles = makeStyles({
    container: {
        maxWidth: '1000px',
        margin: '0 auto',
        display: 'flex',
        flexDirection: 'column',
        gap: '24px'
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '16px'
    },
    grid: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '16px'
    },
    suiteCard: {
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        padding: '16px'
    },
    categoryRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '4px 0'
    },
    thresholdRow: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        marginBottom: '8px'
    },
    thresholdLabel: {
        minWidth: '90px',
        fontWeight: tokens.fontWeightSemibold
    },
    thresholdValue: {
        minWidth: '40px',
        textAlign: 'right' as const
    },
    actions: {
        display: 'flex',
        gap: '8px',
        justifyContent: 'flex-end'
    },
    metaRow: {
        display: 'flex',
        gap: '16px',
        fontSize: tokens.fontSizeBase200,
        color: tokens.colorNeutralForeground3
    },
    globalGrid: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr 1fr',
        gap: '12px'
    }
});

interface TestCategory {
    enabled: boolean;
    label: string;
}

interface TestSuite {
    enabled: boolean;
    label: string;
    testMatch: string[];
    categories: Record<string, TestCategory>;
}

interface CoverageThresholds {
    branches: number;
    functions: number;
    lines: number;
    statements: number;
}

interface GlobalSettings {
    verbose: boolean;
    bail: boolean;
    maxWorkers: string;
    timeoutMs: number;
    retries: number;
}

interface TestConfig {
    version: number;
    lastUpdated: string;
    updatedBy: string;
    global: GlobalSettings;
    coverage: {
        enabled: boolean;
        thresholds: CoverageThresholds;
        reporters: string[];
    };
    suites: Record<string, TestSuite>;
}

const TestAdmin: React.FC = () => {
    const styles = useStyles();
    const [config, setConfig] = useState<TestConfig | null>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [dirty, setDirty] = useState(false);

    const fetchConfig = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await fetch('/api/admin/test-config');
            if (!res.ok) throw new Error(`Failed to load config: ${res.status}`);
            const data = await res.json();
            setConfig(data);
            setDirty(false);
        } catch (err) {
            setError((err as Error).message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchConfig();
    }, [fetchConfig]);

    const saveConfig = async () => {
        if (!config) return;
        setSaving(true);
        setError(null);
        setSuccess(null);
        try {
            const res = await fetch('/api/admin/test-config', {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(config)
            });
            if (!res.ok) throw new Error(`Save failed: ${res.status}`);
            const data = await res.json();
            setConfig(data.config);
            setDirty(false);
            setSuccess('Configuration saved successfully');
            setTimeout(() => setSuccess(null), 3000);
        } catch (err) {
            setError((err as Error).message);
        } finally {
            setSaving(false);
        }
    };

    const updateSuiteEnabled = (suiteId: string, enabled: boolean) => {
        if (!config) return;
        setConfig({
            ...config,
            suites: {
                ...config.suites,
                [suiteId]: { ...config.suites[suiteId], enabled }
            }
        });
        setDirty(true);
    };

    const updateCategoryEnabled = (suiteId: string, categoryId: string, enabled: boolean) => {
        if (!config) return;
        const suite = config.suites[suiteId];
        setConfig({
            ...config,
            suites: {
                ...config.suites,
                [suiteId]: {
                    ...suite,
                    categories: {
                        ...suite.categories,
                        [categoryId]: { ...suite.categories[categoryId], enabled }
                    }
                }
            }
        });
        setDirty(true);
    };

    const updateThreshold = (key: keyof CoverageThresholds, value: number) => {
        if (!config) return;
        setConfig({
            ...config,
            coverage: {
                ...config.coverage,
                thresholds: { ...config.coverage.thresholds, [key]: value }
            }
        });
        setDirty(true);
    };

    const updateGlobal = (key: keyof GlobalSettings, value: unknown) => {
        if (!config) return;
        setConfig({
            ...config,
            global: { ...config.global, [key]: value }
        });
        setDirty(true);
    };

    if (loading) {
        return (
            <div className={styles.container}>
                <Spinner label="Loading test configuration..." />
            </div>
        );
    }

    if (!config) {
        return (
            <div className={styles.container}>
                <MessageBar intent="error">
                    <MessageBarBody>{error || 'No test configuration found'}</MessageBarBody>
                </MessageBar>
            </div>
        );
    }

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <div>
                    <Title2>Test Administration</Title2>
                    <div className={styles.metaRow}>
                        <span>Last updated: {new Date(config.lastUpdated).toLocaleString()}</span>
                        <span>By: {config.updatedBy}</span>
                        <span>Config v{config.version}</span>
                    </div>
                </div>
                <div className={styles.actions}>
                    <Button
                        icon={<ArrowSync24Regular />}
                        appearance="subtle"
                        onClick={fetchConfig}
                        disabled={saving}
                    >
                        Reload
                    </Button>
                    <Button
                        icon={<Play24Regular />}
                        appearance="primary"
                        onClick={saveConfig}
                        disabled={!dirty || saving}
                    >
                        {saving ? 'Saving...' : 'Save Changes'}
                    </Button>
                </div>
            </div>

            {error && (
                <MessageBar intent="error">
                    <MessageBarBody>{error}</MessageBarBody>
                </MessageBar>
            )}
            {success && (
                <MessageBar intent="success">
                    <MessageBarBody>{success}</MessageBarBody>
                </MessageBar>
            )}

            {/* Global Settings */}
            <Card>
                <CardHeader
                    header={<Title3><Settings24Regular /> Global Settings</Title3>}
                />
                <div className={styles.globalGrid}>
                    <Field label="Verbose output">
                        <Switch
                            checked={config.global.verbose}
                            onChange={(_e, data) => updateGlobal('verbose', data.checked)}
                        />
                    </Field>
                    <Field label="Bail on first failure">
                        <Switch
                            checked={config.global.bail}
                            onChange={(_e, data) => updateGlobal('bail', data.checked)}
                        />
                    </Field>
                    <Field label="Max workers">
                        <Input
                            value={String(config.global.maxWorkers)}
                            onChange={(_e, data) => updateGlobal('maxWorkers', data.value)}
                        />
                    </Field>
                    <Field label="Timeout (ms)">
                        <Input
                            type="number"
                            value={String(config.global.timeoutMs)}
                            onChange={(_e, data) => updateGlobal('timeoutMs', parseInt(data.value, 10) || 10000)}
                        />
                    </Field>
                    <Field label="Retries for flaky tests">
                        <Input
                            type="number"
                            value={String(config.global.retries)}
                            onChange={(_e, data) => updateGlobal('retries', parseInt(data.value, 10) || 0)}
                        />
                    </Field>
                </div>
            </Card>

            {/* Coverage Thresholds */}
            <Card>
                <CardHeader
                    header={<Title3>Coverage Thresholds</Title3>}
                    action={
                        <Switch
                            label="Coverage enabled"
                            checked={config.coverage.enabled}
                            onChange={(_e, data) => {
                                setConfig({ ...config, coverage: { ...config.coverage, enabled: data.checked } });
                                setDirty(true);
                            }}
                        />
                    }
                />
                {(['branches', 'functions', 'lines', 'statements'] as const).map((key) => (
                    <div key={key} className={styles.thresholdRow}>
                        <span className={styles.thresholdLabel}>{key.charAt(0).toUpperCase() + key.slice(1)}</span>
                        <Slider
                            min={0}
                            max={100}
                            step={5}
                            value={config.coverage.thresholds[key]}
                            onChange={(_e, data) => updateThreshold(key, data.value)}
                            style={{ flex: 1 }}
                        />
                        <span className={styles.thresholdValue}>{config.coverage.thresholds[key]}%</span>
                    </div>
                ))}
                <Field label="Coverage reporters">
                    <Select
                        value={config.coverage.reporters.join(', ')}
                        onChange={(_e, data) => {
                            setConfig({
                                ...config,
                                coverage: { ...config.coverage, reporters: data.value.split(',').map((s: string) => s.trim()) }
                            });
                            setDirty(true);
                        }}
                    >
                        <option value="text, text-summary">text, text-summary</option>
                        <option value="text, lcov">text, lcov</option>
                        <option value="text, html">text, html</option>
                        <option value="text, text-summary, lcov, html">All reporters</option>
                    </Select>
                </Field>
            </Card>

            {/* Test Suites */}
            <Title3>Test Suites</Title3>
            <div className={styles.grid}>
                {Object.entries(config.suites).map(([suiteId, suite]) => (
                    <Card key={suiteId}>
                        <div className={styles.suiteCard}>
                            <div className={styles.categoryRow}>
                                <div>
                                    <Body1 style={{ fontWeight: tokens.fontWeightSemibold }}>{suite.label}</Body1>
                                    <Badge
                                        appearance="filled"
                                        color={suite.enabled ? 'success' : 'danger'}
                                        icon={suite.enabled ? <CheckmarkCircle24Regular /> : <DismissCircle24Regular />}
                                        style={{ marginLeft: 8 }}
                                    >
                                        {suite.enabled ? 'Enabled' : 'Disabled'}
                                    </Badge>
                                </div>
                                <Switch
                                    checked={suite.enabled}
                                    onChange={(_e, data) => updateSuiteEnabled(suiteId, data.checked)}
                                />
                            </div>

                            {Object.entries(suite.categories).map(([catId, cat]) => (
                                <div key={catId} className={styles.categoryRow}>
                                    <Body1>{cat.label}</Body1>
                                    <Switch
                                        checked={cat.enabled}
                                        onChange={(_e, data) => updateCategoryEnabled(suiteId, catId, data.checked)}
                                        disabled={!suite.enabled}
                                    />
                                </div>
                            ))}
                        </div>
                    </Card>
                ))}
            </div>
        </div>
    );
};

export default TestAdmin;
